import React from "react";
import SeoHead from "../seo/SeoHead";
import ServicePageShell from "../ui/ServicePageShell";
import { ServiceTag } from "../ui/ServiceTag";
import CallButton from "../ui/CallButton";
import DirectionsButton from "../ui/DirectionsButton";
import BusinessHours from "../ui/BusinessHours";
import ReviewSection from "../ui/ReviewSection";
import PageBreadcrumb from "../seo/PageBreadcrumb";
import { Link } from "react-router-dom";
import { PHONE_DISPLAY } from "../../lib/businessInfo";
import heroImg from "../../assets/ElectricalDiag.png";

const bulletClass =
  "flex items-start gap-2 text-sm text-slate-700 sm:text-base leading-relaxed";

const CheckEngineLight = () => {
  return (
    <>
      <SeoHead
        title="Check Engine Light Repair & Diagnostics | Temecula"
        description="Check engine light on? Get accurate diagnostics and repair in Temecula. We pinpoint the cause, explain your options, and fix it right at Advanced Automotive Smog."
        path="/check-engine-light"
      />

      <ServicePageShell>
        <PageBreadcrumb
          label="Check Engine Light"
          path="/check-engine-light"
          description="Check engine light diagnostics and repair in Temecula. We pinpoint the cause, explain your options, and fix it right."
          serviceType="Check engine light diagnostics"
        />
        <div className="flex min-w-0 flex-col gap-8 md:flex-row md:items-start md:gap-14">
          <div className="order-1 flex w-full min-w-0 justify-center md:order-none md:w-2/5">
            <div className="relative w-full max-w-xs sm:max-w-sm">
              <div className="absolute -inset-3 rounded-3xl bg-teal-600/10 blur-xl" />
              <img
                src={heroImg}
                alt="Check engine light diagnostics and electrical testing"
                className="relative aspect-[4/3] h-auto w-full max-h-[14rem] rounded-3xl border border-slate-200 object-cover object-center shadow-xl sm:aspect-auto sm:max-h-none"
              />
            </div>
          </div>

          <div className="w-full min-w-0 md:w-3/5">
            <ServiceTag>Diagnostics · Temecula</ServiceTag>
            <h1 className="mb-3 text-[1.35rem] font-semibold leading-snug tracking-tight text-navy-900 sm:text-3xl md:text-4xl">
              Check Engine Light Service
            </h1>
            <p className="mb-6 text-sm leading-relaxed text-slate-700 sm:text-base">
              A lit check engine light means your vehicle stored a fault code.
              We scan, test, and verify the root cause—so you are not guessing
              with parts or clearing codes that will return.
            </p>
            <CallButton className="px-8 py-3 text-base" />
          </div>
        </div>

        <section className="mt-14 border-t border-slate-200 pt-12">
          <h2 className="mb-4 text-xl font-semibold text-navy-900 sm:text-2xl">
            Signs You May Need This Service
          </h2>
          <ul className="space-y-2.5">
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>Check engine, service engine soon, or emissions-related warning lights</span>
            </li>
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>Strange noises tied to misfires or exhaust issues</span>
            </li>
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>Performance issues: stalling, hesitation, or reduced fuel economy</span>
            </li>
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>Failed smog test or readiness monitor problems</span>
            </li>
          </ul>
        </section>

        <section className="mt-12 border-t border-slate-200 pt-12">
          <h2 className="mb-4 text-xl font-semibold text-navy-900 sm:text-2xl">
            Services Included
          </h2>
          <ul className="space-y-2.5">
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>OBD-II code retrieval and code history review</span>
            </li>
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>Pinpoint testing for sensors, ignition, and fuel systems</span>
            </li>
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>Emissions-related diagnostics and repairs</span>
            </li>
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>Repairs and reset procedures after verification</span>
            </li>
            <li className={bulletClass}>
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-600" />
              <span>Documentation of findings for your records</span>
            </li>
          </ul>
        </section>

        <section className="mt-12 border-t border-slate-200 pt-12">
          <h2 className="mb-4 text-xl font-semibold text-navy-900 sm:text-2xl">
            Why Choose Us
          </h2>
          <p className="text-sm leading-relaxed text-slate-700 sm:text-base">
            We combine{" "}
            <Link
              to="/services/smog-emissions-services"
              className="text-teal-600 hover:underline"
            >
              smog and emissions
            </Link>{" "}
            expertise with general repair, which helps when your check engine
            light is emissions-related. You get straight answers, fair
            recommendations, and repairs aimed at lasting results—not just
            turning the light off.
          </p>
        </section>

        <BusinessHours tone="light" className="mt-12" />
        <ReviewSection tone="light" className="mt-12" />

        <section className="mt-12 rounded-2xl border border-slate-200 bg-teal-50/80 p-6 text-center shadow-sm ring-1 ring-teal-600/10 sm:p-10">
          <h2 className="text-lg font-semibold text-navy-900 sm:text-xl">
            Diagnose your check engine light today
          </h2>
          <p className="mt-2 text-sm text-slate-700 sm:text-base">
            Call {PHONE_DISPLAY} or tap directions to visit our shop on Rio Nedo.
          </p>
          <div className="mt-6 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center">
            <CallButton className="px-8 py-3 text-base" />
            <DirectionsButton className="px-8 py-3 text-base" />
          </div>
        </section>
      </ServicePageShell>
    </>
  );
};

export default CheckEngineLight;
